
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useManagerPermissions } from '@/hooks/useManagerPermissions';
import { toast } from 'sonner';

interface TeamVisibility {
  id: string;
  name: string;
  isVisible: boolean;
}

interface TeamVisibilityGridProps {
  teamId: string;
  weekStartDate?: Date;
  onVisibilityChange?: (teamId: string, date: Date, isVisible: boolean) => Promise<boolean | void>;
}

export const TeamVisibilityGrid: React.FC<TeamVisibilityGridProps> = ({
  teamId,
  weekStartDate = new Date(),
  onVisibilityChange,
}) => {
  const { t } = useLanguage();
  const [updatingTeamId, setUpdatingTeamId] = useState<string | null>(null);
  const [teams, setTeams] = useState<TeamVisibility[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { isAdmin, isManager } = useUserRoles();
  const { canManageTeam } = useManagerPermissions(teamId);
  
  // Check if user has permission to manage this team
  const hasPermissionToManage = isAdmin || isManager || canManageTeam;

  useEffect(() => {
    const fetchTeamVisibility = async () => {
      setIsLoading(true);
      try {
        // Fetch the team
        const { data: teamData, error: teamError } = await supabase
          .from('teams')
          .select('id, name')
          .eq('id', teamId)
          .single();

        if (teamError) {
          console.error("Error fetching team visibility:", teamError);
          return;
        }

        if (teamData) {
          // Check visibility from week_visibility table
          const { data: visibilityData } = await supabase
            .from('week_visibility')
            .select('is_visible')
            .eq('team_id', teamId)
            .eq('week_start_date', format(weekStartDate, 'yyyy-MM-dd'))
            .single();

          setTeams([{
            id: teamData.id,
            name: teamData.name,
            isVisible: visibilityData?.is_visible ?? true
          }]);
        }
      } catch (error) {
        console.error("Error in fetchTeamVisibility:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (teamId) {
      fetchTeamVisibility();
    }
  }, [teamId, weekStartDate]);

  const handleToggleVisibility = async (team: TeamVisibility) => {
    if (!hasPermissionToManage) {
      toast.error(t('noPermissionToChangeVisibility') || 'You do not have permission to change visibility');
      return;
    }
    
    if (!onVisibilityChange) return;
    
    try {
      setUpdatingTeamId(team.id);
      const result = await onVisibilityChange(team.id, weekStartDate, !team.isVisible);
      
      // Only update local state if the operation was successful
      if (result !== false) {
        setTeams(prev => prev.map(t => 
          t.id === team.id ? { ...t, isVisible: !t.isVisible } : t
        ));
      }
    } catch (error) {
      console.error("Error toggling visibility:", error);
    } finally {
      setUpdatingTeamId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (teams.length === 0) {
    return null;
  }

  // Don't show controls if user doesn't have permission
  if (!hasPermissionToManage) {
    return (
      <div className="my-6">
        <h3 className="text-lg font-medium mb-3">
          {t('programVisibility') || 'Program Visibility'} - {format(weekStartDate, 'dd/MM/yyyy')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {teams.map((team) => (
            <div
              key={team.id}
              className="flex justify-between items-center border rounded-md p-2 bg-gray-50"
            >
              <span className="truncate">{team.name}</span>
              <div className="flex items-center">
                {team.isVisible ? (
                  <Eye className="h-4 w-4 text-green-500 ml-2" />
                ) : (
                  <EyeOff className="h-4 w-4 text-red-500 ml-2" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="my-6">
      <h3 className="text-lg font-medium mb-3">
        {t('programVisibility') || 'Program Visibility'} - {format(weekStartDate, 'dd/MM/yyyy')}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
        {teams.map((team) => (
          <Button
            key={team.id}
            variant="outline"
            size="sm"
            className={`flex justify-between items-center ${
              team.isVisible ? 'border-green-500 hover:border-green-600' : 'border-red-500 hover:border-red-600'
            }`}
            onClick={() => handleToggleVisibility(team)}
            disabled={updatingTeamId === team.id}
          >
            <span className="truncate">{team.name}</span>
            {updatingTeamId === team.id ? (
              <Loader2 className="h-4 w-4 animate-spin ml-2" />
            ) : team.isVisible ? (
              <Eye className="h-4 w-4 text-green-500 flex-shrink-0 ml-2" />
            ) : (
              <EyeOff className="h-4 w-4 text-red-500 flex-shrink-0 ml-2" />
            )}
          </Button>
        ))}
      </div>
    </div>
  );
};
